import { useEffect, useMemo, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { useSettings } from "@/context/SettingsContext";
import { SessionCard } from "@/components/SessionCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarDays, CalendarPlus, LockKeyhole, Users, Check } from "lucide-react";
import { googleCalUrl, downloadIcs } from "@/lib/calendarExport";
import api from "@/lib/api";

const UNIVERSES = ["all", "fitness", "pilates", "kids"];
const LOCALE = { es: "es-ES", en: "en-US", fr: "fr-FR", ru: "ru-RU" };
const PANAMA_TZ = "America/Panama";
const ROW_H = 56; // px per hour

// ---- Panama-local helpers (Panama is fixed UTC-5, no DST) ----
function pParts(iso) {
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: PANAMA_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const o = {};
  for (const p of f.formatToParts(new Date(iso))) o[p.type] = p.value;
  const hour = o.hour === "24" ? 0 : +o.hour;
  return { date: `${o.year}-${o.month}-${o.day}`, hour, minute: +o.minute };
}
function panamaToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: PANAMA_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}
const dateAtNoon = (s) => new Date(s + "T12:00:00Z");
function addDays(s, n) { const d = dateAtNoon(s); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function mondayOf(s) {
  const d = dateAtNoon(s);
  const dow = d.getUTCDay(); // 0=Sun..6=Sat
  return addDays(s, -dow); // week starts on Sunday
}
// Panama midnight of a date == that date 05:00Z
const dayStartZ = (s) => `${s}T05:00:00.000Z`;

// interval layout within a single day (cluster-based lane assignment)
function layoutDay(items) {
  const sorted = [...items].sort((a, b) => a._s - b._s || a._e - b._e);
  const out = [];
  let cluster = [], clusterEnd = -1;
  const flush = () => {
    const laneEnds = [];
    cluster.forEach((it) => {
      let idx = laneEnds.findIndex((e) => e <= it._s);
      if (idx === -1) { idx = laneEnds.length; laneEnds.push(it._e); } else laneEnds[idx] = it._e;
      it._lane = idx;
    });
    cluster.forEach((it) => (it._lanes = laneEnds.length));
    out.push(...cluster); cluster = [];
  };
  sorted.forEach((it) => {
    if (cluster.length && it._s >= clusterEnd) { flush(); clusterEnd = -1; }
    cluster.push(it); clusterEnd = Math.max(clusterEnd, it._e);
  });
  if (cluster.length) flush();
  return out;
}

export function ScheduleCalendar({ onBook, onWaitlist, onCancel, onSessionClick, reloadKey, bookingBadges = true, showCounts = false }) {
  const { t, tl, lang } = useI18n();
  const { settings } = useSettings();
  const [sessions, setSessions] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [universe, setUniverse] = useState("all");
  const [coachId, setCoachId] = useState("all");
  const [view, setView] = useState("week"); // week | day
  const [anchor, setAnchor] = useState(panamaToday());
  const [selected, setSelected] = useState(null);
  const locale = LOCALE[lang] || "es-ES";

  const days = useMemo(() => {
    if (view === "day") return [anchor];
    const mon = mondayOf(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(mon, i));
  }, [view, anchor]);

  const load = async () => {
    setLoading(true);
    const params = { start: dayStartZ(days[0]), end: dayStartZ(addDays(days[days.length - 1], 1)) };
    if (universe === "fitness" || universe === "pilates") params.universe = universe;
    if (coachId !== "all") params.coach_id = coachId;
    try {
      const { data } = await api.get("/sessions", { params });
      const list = universe === "kids" ? data.filter((s) => (s.class_type?.credit_type || "").startsWith("youth")) : data;
      setSessions(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { api.get("/coaches").then(({ data }) => setCoaches(data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [universe, coachId, view, anchor, reloadKey]);

  // enrich sessions with panama-local position data + group by day
  const byDay = useMemo(() => {
    const g = {};
    days.forEach((d) => (g[d] = []));
    sessions.forEach((s) => {
      const p = pParts(s.start);
      if (!(p.date in g)) return;
      const startMin = p.hour * 60 + p.minute;
      const durMin = Math.max(15, Math.round((new Date(s.end) - new Date(s.start)) / 60000) || (s.class_type?.duration_min ?? 60));
      g[p.date].push({ ...s, _s: startMin, _e: startMin + durMin, _dur: durMin });
    });
    Object.keys(g).forEach((d) => (g[d] = layoutDay(g[d])));
    return g;
  }, [sessions, days]);

  const { hStart, hEnd } = useMemo(() => {
    let min = 24, max = 0;
    sessions.forEach((s) => {
      const p = pParts(s.start);
      const startH = p.hour + p.minute / 60;
      const durMin = Math.round((new Date(s.end) - new Date(s.start)) / 60000) || 60;
      min = Math.min(min, Math.floor(startH));
      max = Math.max(max, Math.ceil(startH + durMin / 60));
    });
    if (min > max) return { hStart: 6, hEnd: 22 };
    return { hStart: Math.min(min, 7), hEnd: Math.max(max, 20) };
  }, [sessions]);

  const hours = Array.from({ length: hEnd - hStart }, (_, i) => hStart + i);
  const gridHeight = (hEnd - hStart) * ROW_H;
  const todayStr = panamaToday();

  const navLabel = view === "day"
    ? dateAtNoon(anchor).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })
    : `${dateAtNoon(days[0]).toLocaleDateString(locale, { day: "numeric", month: "short", timeZone: "UTC" })} – ${dateAtNoon(days[6]).toLocaleDateString(locale, { day: "numeric", month: "short", timeZone: "UTC" })}`;

  const step = view === "day" ? 1 : 7;
  const shift = (n) => setAnchor(addDays(anchor, n * step));

  const handleBook = (s) => { setSelected(null); onBook?.(s); };
  const handleWaitlist = (s) => { setSelected(null); onWaitlist?.(s); };
  const handleCancel = (s) => { setSelected(null); onCancel?.(s); };

  // Booking window: date at which registration opens (null if already open or badges disabled)
  const bookingPolicy = settings?.booking_policy || {};
  const opensOnLabel = (s) => {
    if (!bookingBadges) return null;
    const uni = s.universe || "fitness";
    const openDays = bookingPolicy[`${uni}_open_days`] ?? bookingPolicy.fitness_open_days ?? 7;
    const opensMs = new Date(s.start).getTime() - openDays * 86400000;
    if (Date.now() >= opensMs) return null;
    const date = new Intl.DateTimeFormat(locale, { timeZone: PANAMA_TZ, day: "2-digit", month: "2-digit" }).format(new Date(opensMs));
    return t("schedule.opensOn").replace("{date}", date);
  };

  const calPayload = (s) => ({
    title: `${tl(s.class_type?.name) || "Frenchies"} — ${s.universe === "pilates" ? "Pilates" : "Fitness"}`,
    start: s.start,
    end: s.end,
    details: [s.coach?.name && `Coach: ${s.coach.name}`, "Frenchies Fitness & Pilates"].filter(Boolean).join(" · "),
    location: s.room?.name || "Frenchies",
  });

  return (
    <div>
      {/* Controls */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-full bg-secondary p-1" data-testid="schedule-view-toggle">
          {["day", "week"].map((v) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {t(`schedule.${v}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button data-testid="cal-prev" onClick={() => shift(-1)} className="grid h-9 w-9 place-items-center rounded-full border border-border transition-colors hover:bg-secondary">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button data-testid="cal-today" onClick={() => setAnchor(panamaToday())}
            className="h-9 rounded-full border border-border px-4 text-sm font-medium transition-colors hover:bg-secondary">
            {t("schedule.today")}
          </button>
          <button data-testid="cal-next" onClick={() => shift(1)} className="grid h-9 w-9 place-items-center rounded-full border border-border transition-colors hover:bg-secondary">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <span className="font-display text-sm font-semibold capitalize text-foreground" data-testid="cal-range-label">{navLabel}</span>

        <div className="ml-auto flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-full bg-secondary p-1">
            {UNIVERSES.map((u) => (
              <button key={u} data-testid={`filter-universe-${u}`} onClick={() => setUniverse(u)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${universe === u ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {u === "all" ? t("common.all") : u === "fitness" ? "Fitness" : u === "pilates" ? "Pilates" : "Kids"}
              </button>
            ))}
          </div>
          <select data-testid="filter-coach" value={coachId} onChange={(e) => setCoachId(e.target.value)}
            className="h-9 rounded-full border border-border bg-card px-3 text-xs">
            <option value="all">{t("schedule.filterCoach")}: {t("common.all")}</option>
            {coaches.map((c) => <option key={c.coach_id} value={c.coach_id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" />{t("schedule.clickHint")}</p>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card" data-testid="calendar-grid">
          <div style={{ minWidth: view === "week" ? 760 : 320 }}>
            {/* Day headers */}
            <div className="flex border-b border-border">
              <div className="w-12 shrink-0" />
              {days.map((d) => {
                const dd = dateAtNoon(d);
                const isToday = d === todayStr;
                return (
                  <div key={d} className="flex-1 border-l border-border py-2 text-center">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{dd.toLocaleDateString(locale, { weekday: "short", timeZone: "UTC" })}</div>
                    <div className={`mx-auto mt-0.5 grid h-7 w-7 place-items-center rounded-full text-sm font-semibold ${isToday ? "bg-primary text-primary-foreground" : "text-foreground"}`}>
                      {dd.toLocaleDateString(locale, { day: "numeric", timeZone: "UTC" })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Time grid */}
            <div className="flex">
              {/* hour gutter */}
              <div className="w-12 shrink-0">
                {hours.map((h) => (
                  <div key={h} style={{ height: ROW_H }} className="relative">
                    <span className="absolute -top-2 right-1.5 text-[10px] text-muted-foreground">{String(h).padStart(2, "0")}:00</span>
                  </div>
                ))}
              </div>

              {/* day columns */}
              {days.map((d) => (
                <div key={d} className="relative flex-1 border-l border-border" style={{ height: gridHeight }}>
                  {hours.map((h) => (
                    <div key={h} style={{ top: (h - hStart) * ROW_H }} className="absolute inset-x-0 border-t border-border/60" />
                  ))}
                  {byDay[d]?.map((s) => {
                    const isPilates = s.universe === "pilates";
                    const accent = isPilates ? "hsl(217 91% 60%)" : (s.class_type?.credit_type || "").startsWith("youth") ? "hsl(43 74% 49%)" : "hsl(355 85% 55%)";
                    const top = (s._s / 60 - hStart) * ROW_H;
                    const height = Math.max(26, (s._dur / 60) * ROW_H - 2);
                    const w = 100 / (s._lanes || 1);
                    const full = (s.spots_left ?? 0) <= 0;
                    const opensLabel = opensOnLabel(s);
                    return (
                      <button
                        key={s.session_id}
                        data-testid={`cal-session-${s.session_id}`}
                        onClick={() => (onSessionClick ? onSessionClick(s) : setSelected(s))}
                        title={tl(s.class_type?.name)}
                        style={{ top: top + 1, height, left: `calc(${s._lane * w}% + 2px)`, width: `calc(${w}% - 4px)`, background: `${accent}1a`, borderColor: accent, opacity: full ? 0.6 : 1 }}
                        className="absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-1 text-left transition-transform hover:z-10 hover:scale-[1.02]"
                      >
                        <div className="flex items-center gap-1 truncate text-[11px] font-semibold leading-tight" style={{ color: accent }}>
                          {s.booked_by_me && <Check className="h-3 w-3 shrink-0 text-emerald-500" />}
                          <span className="truncate">{tl(s.class_type?.name) || "Clase"}</span>
                        </div>
                        {height > 34 && (
                          <div className="truncate text-[10px] leading-tight text-muted-foreground">
                            {new Date(s.start).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", timeZone: PANAMA_TZ })}
                          </div>
                        )}
                        {height > 50 && s.coach?.name && (
                          <div className="truncate text-[10px] leading-tight text-muted-foreground">{s.coach.name}</div>
                        )}
                        {showCounts && (
                          <div data-testid={`cal-count-${s.session_id}`}
                            className={`flex items-center gap-0.5 text-[10px] font-bold leading-tight ${full ? "text-destructive" : "text-foreground/70"}`}>
                            <Users className="h-2.5 w-2.5 shrink-0" />{(s.capacity ?? 0) - (s.spots_left ?? 0)}/{s.capacity ?? 0}
                          </div>
                        )}
                        {opensLabel && (
                          <div data-testid={`cal-opens-${s.session_id}`} className="mt-0.5 inline-flex max-w-full items-center gap-0.5 truncate rounded-sm bg-foreground/10 px-1 py-px text-[9px] font-medium leading-tight text-muted-foreground">
                            <LockKeyhole className="h-2.5 w-2.5 shrink-0" />{opensLabel}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!loading && sessions.length === 0 && (
        <div className="mt-4 rounded-2xl border border-dashed border-border py-10 text-center text-muted-foreground">{t("schedule.noClasses")}</div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="session-detail-dialog">
          <DialogHeader>
            <DialogTitle>{t("schedule.details")}</DialogTitle>
          </DialogHeader>
          {selected && <SessionCard session={selected} onBook={handleBook} onWaitlist={handleWaitlist} onCancel={handleCancel} />}
          {selected && (
            <div className="mt-1">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarPlus className="h-3.5 w-3.5" />{t("schedule.addCalendar")}
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href={googleCalUrl(calPayload(selected))}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="add-google-cal"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-secondary"
                >
                  {t("schedule.googleCal")}
                </a>
                <button
                  type="button"
                  data-testid="add-ics-cal"
                  onClick={() => downloadIcs(calPayload(selected))}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-secondary"
                >
                  {t("schedule.icsFile")}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
